import { useQuery } from "@tanstack/react-query";

import { webAnalyticsQueryKeys } from "./queryKeys";

import type {
  WebAnalyticsFilterCounts,
  WebAnalyticsFilters,
  WebAnalyticsMetrics,
  WebAnalyticsReportRow,
} from "@/features/web-analytics/types";
import { instance } from "@/lib/axios/instance";
import type { PagedResponse } from "@/types/response/PagedResponse";
import { formatLocalDateOnly } from "@/utils/dateUtils";

type WebAnalyticsDataParams = {
  startDate?: Date;
  endDate?: Date;
  clientId?: string;
  filters?: WebAnalyticsFilters;
  searchValue?: string;
};

type WebAnalyticsAggregatedParams = WebAnalyticsDataParams & {
  page?: number;
  pageSize?: number;
  sortColumn?: string | null;
  sortDirection?: "asc" | "desc" | null;
};

/** Only send clientId when set — client users are auto-scoped by the backend via their JWT. */
function clientParam(clientId?: string): { clientId?: string } {
  return clientId?.trim() ? { clientId: clientId.trim() } : {};
}

/** Spread selected multi-value dimension filters into repeated query params (dropping empties). */
function filterParams(filters: WebAnalyticsFilters | undefined): Record<string, string[]> {
  if (!filters) return {};
  return Object.fromEntries(
    Object.entries(filters).filter(([, values]) => (values?.length ?? 0) > 0),
  ) as Record<string, string[]>;
}

/**
 * GET /api/v2/WebAnalytics/Aggregated — the source-breakdown table, grouped by
 * (referrerType, trackingSource, platformSource) with backend-computed rates.
 */
export function useWebAnalyticsAggregated(params: WebAnalyticsAggregatedParams) {
  const {
    startDate,
    endDate,
    clientId,
    filters,
    searchValue,
    page = 1,
    pageSize = 10,
    sortColumn,
    sortDirection,
  } = params;

  const dimensions = filterParams(filters);
  const keyParams = {
    startDate: startDate ? formatLocalDateOnly(startDate) : undefined,
    endDate: endDate ? formatLocalDateOnly(endDate) : undefined,
    clientId,
    filters: dimensions,
    searchValue,
    page,
    pageSize,
    sortColumn,
    sortDirection,
  };

  return useQuery({
    queryKey: webAnalyticsQueryKeys.aggregated(keyParams),
    queryFn: () =>
      instance
        .get<PagedResponse<WebAnalyticsReportRow>>("/api/v2/WebAnalytics/Aggregated", {
          params: {
            dateFrom: keyParams.startDate,
            dateTo: keyParams.endDate,
            ...clientParam(clientId),
            ...dimensions,
            search: searchValue || undefined,
            page,
            pageSize,
            sortColumn,
            sortDirection,
          },
        })
        .then((res) => res.data),
  });
}

/**
 * GET /api/v2/WebAnalytics/FilterCounts — the drawer facets.
 *
 * Callers should NOT pass `filters`: the option lists are scoped by date range, client and search
 * only, so the drawer always offers every value available in the period. Sending active selections
 * cascades them across dimensions, and reopening the drawer then shows a shrunken list. The backend
 * still supports per-dimension exclusion defensively, but the pages don't rely on it.
 */
export function useWebAnalyticsFilterCounts(params: WebAnalyticsDataParams) {
  const { startDate, endDate, clientId, filters, searchValue } = params;

  const dimensions = filterParams(filters);
  const keyParams = {
    startDate: startDate ? formatLocalDateOnly(startDate) : undefined,
    endDate: endDate ? formatLocalDateOnly(endDate) : undefined,
    clientId,
    filters: dimensions,
    searchValue,
  };

  return useQuery({
    queryKey: webAnalyticsQueryKeys.filterCounts(keyParams),
    queryFn: () =>
      instance
        .get<WebAnalyticsFilterCounts>("/api/v2/WebAnalytics/FilterCounts", {
          params: {
            dateFrom: keyParams.startDate,
            dateTo: keyParams.endDate,
            ...clientParam(clientId),
            ...dimensions,
            search: searchValue || undefined,
          },
        })
        .then((res) => res.data),
  });
}

/**
 * GET /api/v2/WebAnalytics/Metrics/Aggregated — the top-of-table summary cards, computed over the whole
 * filtered set (same scope/filters as the table, but no paging).
 */
export function useWebAnalyticsMetrics(params: WebAnalyticsDataParams) {
  const { startDate, endDate, clientId, filters, searchValue } = params;

  const dimensions = filterParams(filters);
  const keyParams = {
    startDate: startDate ? formatLocalDateOnly(startDate) : undefined,
    endDate: endDate ? formatLocalDateOnly(endDate) : undefined,
    clientId,
    filters: dimensions,
    searchValue,
  };

  return useQuery({
    queryKey: webAnalyticsQueryKeys.metrics(keyParams),
    queryFn: () =>
      instance
        .get<WebAnalyticsMetrics>("/api/v2/WebAnalytics/Metrics/Aggregated", {
          params: {
            dateFrom: keyParams.startDate,
            dateTo: keyParams.endDate,
            ...clientParam(clientId),
            ...dimensions,
            search: searchValue || undefined,
          },
        })
        .then((res) => res.data),
  });
}

/**
 * Live "visits found" count for the filter-drawer header, invoked with the drawer's pending
 * (not-yet-applied) selection so the total updates as the user toggles checkboxes. Reuses the metrics
 * endpoint's totalVisits with the pending dimensions merged over the current date range + search.
 */
export function useWebAnalyticsFilteredCount(
  baseParams: Omit<WebAnalyticsDataParams, "filters">,
  pendingFilters: WebAnalyticsFilters,
): { count: number; isLoading: boolean } {
  const query = useWebAnalyticsMetrics({ ...baseParams, filters: pendingFilters });
  // isLoading, not isFetching: with no pending change this shares the page's metrics cache entry, so a
  // background refetch there would otherwise blank an already-known count into a skeleton.
  return { count: query.data?.totalVisits ?? 0, isLoading: query.isLoading };
}
